import { createContext, createElement, useContext } from "react";
import { useInfiniteQuery, useMutation, useQuery, useQueryClient, } from "@tanstack/react-query";
import { MimirClient } from "./client.js";
const MimirClientContext = createContext(null);
export function MimirClientProvider({ client, children }) {
    return createElement(MimirClientContext.Provider, { value: client }, children);
}
export function useMimirClient() {
    const client = useContext(MimirClientContext);
    if (!client) {
        throw new Error("useMimirClient must be used inside <MimirClientProvider>");
    }
    return client;
}
/* Ingest */
export function useIngest() {
    const client = useMimirClient();
    return useMutation({
        mutationFn: (input) => client.ingest(input),
    });
}
/* Capture */
export function useCaptureText() {
    const client = useMimirClient();
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (input) => client.captureText(input),
        onSuccess: () => queryClient.invalidateQueries({ queryKey: ["documents"] }),
    });
}
export function useCaptureNote() {
    const client = useMimirClient();
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (input) => client.captureNote(input),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["documents"] });
            queryClient.invalidateQueries({ queryKey: ["notes"] });
        },
    });
}
export function useCaptureUrl() {
    const client = useMimirClient();
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (input) => client.captureUrl(input),
        onSuccess: () => queryClient.invalidateQueries({ queryKey: ["documents"] }),
    });
}
export function useCaptureFile() {
    const client = useMimirClient();
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (input) => client.captureFile(input),
        onSuccess: () => queryClient.invalidateQueries({ queryKey: ["documents"] }),
    });
}
/* Query */
export function useQuery_() {
    const client = useMimirClient();
    return useMutation({
        mutationFn: ({ request, signal }) => client.query(request, signal),
    });
}
/* Conversations */
export function useConversations() {
    const client = useMimirClient();
    return useQuery({
        queryKey: ["conversations"],
        queryFn: () => client.listConversations(),
    });
}
export function useConversation(id) {
    const client = useMimirClient();
    return useQuery({
        queryKey: ["conversations", id],
        queryFn: () => client.getConversation(id),
        enabled: !!id,
    });
}
export function useDeleteConversation() {
    const client = useMimirClient();
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (id) => client.deleteConversation(id),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["conversations"] });
        },
    });
}
/* Documents */
export function useDocuments(params) {
    const client = useMimirClient();
    return useInfiniteQuery({
        queryKey: ["documents", params],
        queryFn: ({ pageParam }) => client.listDocuments({ ...params, cursor: pageParam }),
        initialPageParam: undefined,
        getNextPageParam: (lastPage) => lastPage.next_cursor ?? undefined,
    });
}
export function useDocumentStats() {
    const client = useMimirClient();
    return useQuery({
        queryKey: ["documents", "stats"],
        queryFn: () => client.getDocumentStats(),
        staleTime: 60 * 1000, // 1 min — counts don't need to be real-time
    });
}
export function useGraph() {
    const client = useMimirClient();
    return useQuery({
        queryKey: ["documents", "graph"],
        queryFn: () => client.getGraph(),
        staleTime: 5 * 60 * 1000, // 5 min — graph changes slowly
    });
}
export function useDocument(id) {
    const client = useMimirClient();
    const queryClient = useQueryClient();
    return useQuery({
        queryKey: ["documents", id],
        queryFn: () => client.getDocument(id),
        enabled: !!id,
        retry: false,
        // Poll while background processing is still running (no chunks yet, not a note).
        // Stops automatically once chunks arrive.
        refetchInterval: (query) => {
            const data = query.state.data;
            if (data && data.chunk_count === 0 && data.content_type !== "note" && data.summary === null) {
                return 3000;
            }
            return false;
        },
        refetchIntervalInBackground: false,
        placeholderData: () => {
            // Search every cached infinite document list for the matching summary.
            const caches = queryClient.getQueriesData({ queryKey: ["documents"] });
            for (const [, data] of caches) {
                if (!data?.pages)
                    continue;
                for (const page of data.pages) {
                    const found = page.data.find((d) => d.id === id);
                    if (found)
                        return { ...found, content: null };
                }
            }
            return undefined;
        },
    });
}
export function usePatchDocument() {
    const client = useMimirClient();
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ id, patch }) => client.patchDocument(id, patch),
        onSuccess: (data) => {
            queryClient.invalidateQueries({ queryKey: ["documents"] });
            queryClient.invalidateQueries({ queryKey: ["notes"] });
            queryClient.setQueryData(["documents", data.id], data);
        },
    });
}
export function useDeleteDocument() {
    const client = useMimirClient();
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (id) => client.deleteDocument(id),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["documents"] });
            queryClient.invalidateQueries({ queryKey: ["notes"] });
        },
    });
}
/* Notes */
export function useNotes(params) {
    const client = useMimirClient();
    return useInfiniteQuery({
        queryKey: ["notes", params],
        queryFn: ({ pageParam }) => client.listNotes({ ...params, cursor: pageParam }),
        initialPageParam: undefined,
        getNextPageParam: (lastPage) => lastPage.next_cursor ?? undefined,
    });
}
export function useReorderNotes() {
    const client = useMimirClient();
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (ids) => client.reorderNotes(ids),
        onSuccess: () => queryClient.invalidateQueries({ queryKey: ["notes"] }),
    });
}
/* User */
export function useCurrentUser() {
    const client = useMimirClient();
    return useQuery({
        queryKey: ["users", "me"],
        queryFn: () => client.getMe(),
        staleTime: 5 * 60 * 1000, // 5 min - profile rarely changes
    });
}
/* Tags */
export function useTags() {
    const client = useMimirClient();
    return useQuery({
        queryKey: ["tags"],
        queryFn: () => client.listTags(),
    });
}
/* Rooms */
export function useRoomsOverview() {
    const client = useMimirClient();
    return useQuery({
        queryKey: ["rooms", "overview"],
        queryFn: () => client.getRoomsOverview(),
    });
}
export function useRooms() {
    const client = useMimirClient();
    return useQuery({
        queryKey: ["rooms"],
        queryFn: () => client.listRooms(),
    });
}
export function useCreateRoom() {
    const client = useMimirClient();
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (input) => client.createRoom(input),
        onSuccess: () => queryClient.invalidateQueries({ queryKey: ["rooms"] }),
    });
}
export function usePatchRoom() {
    const client = useMimirClient();
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ id, patch }) => client.patchRoom(id, patch),
        onSuccess: () => queryClient.invalidateQueries({ queryKey: ["rooms"] }),
    });
}
export function useDeleteRoom() {
    const client = useMimirClient();
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (id) => client.deleteRoom(id),
        onSuccess: () => queryClient.invalidateQueries({ queryKey: ["rooms"] }),
    });
}
/* URL preflight */
export function useCheckUrl(url) {
    const client = useMimirClient();
    return useQuery({
        queryKey: ["url-preflight", url],
        queryFn: () => client.checkUrl(url),
        enabled: url.startsWith("http"),
        staleTime: 60 * 1000, // 1 min — robots.txt rarely changes
        retry: false,
    });
}
//# sourceMappingURL=hooks.js.map